import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Normalize Remittance Document Step
 *
 * Reads structured remittance fields out of a completed OCR result for a
 * single document and normalizes them into a stable `RemittanceDoc` shape.
 * No AI/OCR runs here — extraction happened upstream in
 * `extract_document_content_v2`; this step just maps the raw JSON (which can
 * arrive in two shapes — legacy snake_case nested under `remittance_advice`,
 * or flat camelCase from schema-driven extraction) into a single typed object.
 *
 * Designed for the fanout downstream of `extract_document_content_v2` where
 * one workflow instance runs per document.
 *
 * Note: step type string is preserved as `extract_remittance_from_document`
 * for backward-compatibility with deployed WorkflowDefinition rows referencing
 * the old type. File, export, and human-facing name are the new identity.
 */

const invoiceReferenceSchema = z.object({
  invoiceNumber: z.string(),
  appliedAmount: z.number().optional(),
  grossAmount: z.number().optional(),
  discountAmount: z.number().optional(),
  adjustmentAmount: z.number().optional(),
});

/**
 * A standalone (general) deduction the customer withheld that isn't tied to a
 * specific invoice reference — e.g. a freight/shortage claim stated on the
 * remittance as a lump sum. Invoice-tied deductions live on the
 * `discountAmount`/`adjustmentAmount` of an invoice reference instead.
 */
const deductionSchema = z.object({
  amount: z.number(),
  reason: z.string().optional(),
});

/**
 * The normalized remittance payload — every data field the downstream
 * `create_pending_payment` step needs, in one socket.
 */
export const RemittanceDocSchema = z.object({
  paymentAmount: z.number(),
  paymentDate: z.string().optional(),
  paymentMethod: z.string().optional(),
  referenceNumber: z.string().optional(),
  customerName: z.string().optional(),
  customerEmailAddresses: z.array(z.string()).default([]),
  invoiceReferences: z.array(invoiceReferenceSchema).default([]),
  deductions: z.array(deductionSchema).optional(),
});

/**
 * Customer-side identifiers harvested from the same OCR output. Shape matches
 * `identify_customer.preExtractedIdentifiers` so the two steps can be wired
 * directly via a single JSON edge.
 */
export const CustomerIdentifiersSchema = z.object({
  possibleNames: z.array(z.string()).default([]),
  emailDomains: z.array(z.string()).default([]),
  emailAddresses: z.array(z.string()).default([]),
});

export const normalizeRemittanceDocumentStep = defineStepConfig({
  type: "extract_remittance_from_document",
  name: "Normalize Remittance Document",
  description:
    "Read structured remittance fields from a completed document OCR result and emit a normalized RemittanceDoc plus customer-side identifiers",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The document whose OCR result should be normalized",
    ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email for context",
    ),
  }),

  outputSchema: z.object({
    status: z
      .enum(["success", "extraction_failed"])
      .describe(
        "'success' when remittance fields were normalized; 'extraction_failed' when OCR or required fields are missing",
      ),
    failureReason: z
      .string()
      .optional()
      .describe("Human-readable reason when status is extraction_failed"),
    extractionResultId: z
      .uuid()
      .optional()
      .describe("OCR result ID, present when OCR completed"),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Passthrough of the source document — needed downstream to populate remittance_advice.document_id",
      ),
    remittanceDoc: RemittanceDocSchema.optional().describe(
      "Normalized remittance fields (paymentAmount + metadata + invoice references). Present when status is success.",
    ),
    customerIdentifiers: CustomerIdentifiersSchema.optional().describe(
      "Customer-side identifiers harvested from the OCR output. Shape matches identify_customer.preExtractedIdentifiers.",
    ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
    },
  },
});
